import React, { memo, useEffect, useRef, useState } from 'react'
import { Box, List, ListItem, ListItemIcon, ListItemText, Typography, TextField, Button, Divider } from '@mui/material'
import { GRAY_COLORS } from 'asterX-UI'
import { SendIcon } from 'asterX-UI/Icons/SendIcon'
import { FilePlusIcon } from 'asterX-UI/Icons/FilePlusIcon'
import { CreditCardRefresh } from 'asterX-UI/Icons/CreditCardRefresh'
import { MessageTextSquare } from 'asterX-UI/Icons/MessageTextSquare'
import { EyeIcon, FileSearchIcon } from 'asterX-UI/Icons'
import { useContractStore } from 'context/ContractStore'
import { FileAttachment04 } from 'asterX-UI/Icons/FileAttachment04'
import { FileCheck02 } from 'asterX-UI/Icons/FileCheck02'
import { debounce } from 'lodash'
import { HEAD_UNDERWRITER, SUPER_UNDERWRITER, UNDERWRITER } from 'lib/constants/roles'
import { useAuthStore } from '../../context/AuthStore'

export const CommentHistory = memo(({ readonly }) => {
  const listRef = useRef(null)
  const {
    contractStore: { loanApplicationData, chatHistory, managerWorking },
    contractStoreFunc: { getChatHistory, addComment }
  } = useContractStore()
  const { authStore } = useAuthStore()
  const [comment, setComment] = useState('')
  const [shouldScroll, setShouldScroll] = useState(true)
  const [isEditingComment, setIsEditingComment] = useState(false)
  const isUnderwriter = [HEAD_UNDERWRITER, SUPER_UNDERWRITER, UNDERWRITER].includes(authStore?.isUserRole)

  const getRoleName = data => {
    const roleMapping = {
      /*TODO - CREDIT_ADMIN: data?.actionType,*/
      MANAGER: data?.actionType,
      UNDERWRITER: data?.actionType,
      HEAD_UNDERWRITER: data?.actionType,
      SUPER_UNDERWRITER: data?.actionType,
      ACCOUNTANT: data?.actionType
    }

    return roleMapping[data?.role] || 'Комментарии'
  }

  const handleCommentChange = e => {
    setComment(e.target.value)
  }
  const handleEditing = editing => {
    setIsEditingComment(editing)
  }

  const handleAddComment = debounce(() => {
    if (!loanApplicationData?.id || comment?.trim()?.length <= 2) return
    addComment(loanApplicationData?.id, comment)
    setComment('')
    setShouldScroll(true)
  }, 500)

  const handleKeyPress = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleAddComment()
    }
  }

  const transformedHistoryData = chatHistory.map(item => {
    const iconMapping = {
      'Создана заявка': <FilePlusIcon />,
      'Получен кредитный скоринг': <CreditCardRefresh />,
      'В очереди на рассмотрении': <EyeIcon sx={{ color: GRAY_COLORS[600] }} />,
      'Проверка документов партнера': <FileSearchIcon sx={{ color: GRAY_COLORS[600] }} />,
      'Документы дилера были загружены': <FileAttachment04 sx={{ color: GRAY_COLORS[600] }} />,
      'Принято в работу кредитным администратором': <MessageTextSquare />,
      'Проверка документов клиента': <FileSearchIcon sx={{ color: GRAY_COLORS[600] }} />,
      'Документы клиента были загружены': <FileAttachment04 sx={{ color: GRAY_COLORS[600] }} />,
      'Создан ДДС': <FileCheck02 />,
      Комментарии: <MessageTextSquare />
    }

    const hasRoleAndText = item?.role && item?.text

    return {
      icon: iconMapping[item?.actionType] || <MessageTextSquare />,
      title: hasRoleAndText ? getRoleName(item) : item?.actionType,
      date: new Date(item?.createdDate).toLocaleString('ru-RU'),
      comment: item?.text || null,
      createdBy: item?.createdBy || null
    }
  })

  const formatText = text => {
    if (!text) return null

    return text
      .replace(/<br\s*\/?>/g, '\n')
      .replace(/\\u000A/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\n\s+/g, '\n')
      .trim()
  }

  useEffect(() => {
    if (!loanApplicationData?.id || isEditingComment || managerWorking) return
    const fetchChatHistory = () => getChatHistory(loanApplicationData?.id)
    const interval = setInterval(fetchChatHistory, 10000)
    fetchChatHistory()
    return () => clearInterval(interval)
  }, [loanApplicationData?.id, isEditingComment, managerWorking])

  const prevHistoryLengthRef = useRef(transformedHistoryData?.length || 0)

  useEffect(() => {
    if (transformedHistoryData?.length > prevHistoryLengthRef.current) {
      setShouldScroll(true)
    }

    prevHistoryLengthRef.current = transformedHistoryData?.length
  }, [transformedHistoryData])

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (listRef.current && shouldScroll) {
        listRef.current.scrollTo({
          top: listRef.current.scrollHeight,
          behavior: 'smooth'
        })
        setShouldScroll(false)
      }
    })

    if (listRef.current) {
      observer.observe(listRef.current, { childList: true, subtree: true })
    }

    return () => observer.disconnect()
  }, [shouldScroll])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: 360,
        height: 575,
        borderRadius: 2,
        padding: 4,
        border: `1px solid ${GRAY_COLORS[200]}`,
        overflow: 'hidden'
      }}
    >
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 600,
          marginBottom: 4,
          color: GRAY_COLORS[900],
          flexShrink: 0
        }}
      >
        Комментарии и история
      </Typography>

      <Divider sx={{ marginLeft: '-16px', marginRight: '-16px' }} />

      <Box
        ref={listRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto'
        }}
      >
        <List>
          {transformedHistoryData.map((item, index) => {
            const isSticky = index === 1

            return (
              <Box
                key={index}
                sx={{
                  position: isSticky ? 'sticky' : 'static',
                  top: isSticky ? 0 : 'auto',
                  zIndex: isSticky ? 10 : 'auto',
                  backgroundColor: isSticky ? '#fff' : 'transparent'
                }}
              >
                <ListItem
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    padding: 0
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      border: `1px solid ${GRAY_COLORS[100]}`,
                      borderRadius: 2,
                      backgroundColor: GRAY_COLORS[100],
                      padding: 1,
                      marginTop: 2
                    }}
                  >
                    {item?.icon}
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatText(item?.title)}</Typography>

                        {isUnderwriter && (
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatText(item?.createdBy)}</Typography>
                        )}
                      </Box>
                    }
                    secondary={<Typography sx={{ fontSize: 12 }}>{item?.date}</Typography>}
                  />
                </ListItem>

                {item?.comment && (
                  <Typography sx={{ fontSize: 14, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                    {formatText(item?.comment)}
                  </Typography>
                )}

                <Divider sx={{ my: 1 }} />
              </Box>
            )
          })}
        </List>
      </Box>

      <Divider sx={{ marginLeft: '-16px', marginRight: '-16px' }} />

      {!readonly && (
        <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 4, flexShrink: 0 }}>
          <TextField
            value={comment}
            placeholder="Напишите комментарий..."
            sx={{ marginRight: 1 }}
            onFocus={() => handleEditing(true)}
            onBlur={() => handleEditing(false)}
            onChange={handleCommentChange}
            onKeyDown={handleKeyPress}
            variant="outlined"
            size="small"
            fullWidth
          />
          <Button
            variant="contained"
            color="primary"
            sx={{ minWidth: '40px', padding: '10px' }}
            disabled={comment?.trim()?.length <= 2}
            onClick={handleAddComment}
          >
            <SendIcon />
          </Button>
        </Box>
      )}
    </Box>
  )
})
